import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminCoupons = () => {
  const { toast } = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    minOrderAmount: '0',
    maxDiscount: '',
    startDate: '',
    endDate: '',
    usageLimit: '',
    perUserLimit: '1',
    description: '',
    firstPurchaseOnly: false,
    returningCustomersOnly: false
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/coupons?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Load coupons error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load coupons',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('accessToken');
      const data = {
        ...formData,
        value: parseFloat(formData.value),
        minOrderAmount: parseFloat(formData.minOrderAmount) || 0,
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        perUserLimit: parseInt(formData.perUserLimit) || 1,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString()
      };

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Coupon Updated', description: 'Coupon updated successfully' });
      } else {
        await axios.post(`${API}/coupons`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Coupon Created', description: 'Coupon created successfully' });
      }

      setShowDialog(false);
      resetForm();
      loadCoupons();
    } catch (error) {
      console.error('Save coupon error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save coupon',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value.toString(),
      minOrderAmount: coupon.minOrderAmount.toString(),
      maxDiscount: coupon.maxDiscount?.toString() || '',
      startDate: coupon.startDate.split('T')[0],
      endDate: coupon.endDate.split('T')[0],
      usageLimit: coupon.usageLimit?.toString() || '',
      perUserLimit: coupon.perUserLimit.toString(),
      description: coupon.description || '',
      firstPurchaseOnly: !!coupon.firstPurchaseOnly,
      returningCustomersOnly: !!coupon.returningCustomersOnly
    });
    setShowDialog(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Coupon Deleted', description: 'Coupon deleted successfully' });
      loadCoupons();
    } catch (error) {
      console.error('Delete coupon error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete coupon',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      type: 'percentage',
      value: '',
      minOrderAmount: '0',
      maxDiscount: '',
      startDate: '',
      endDate: '',
      usageLimit: '',
      perUserLimit: '1',
      description: '',
      firstPurchaseOnly: false,
      returningCustomersOnly: false
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Copied', description: `Coupon code "${code}" copied to clipboard` });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (coupon) => {
    const now = new Date();
    const start = new Date(coupon.startDate);
    const end = new Date(coupon.endDate);

    if (coupon.status === 'inactive') {
      return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Inactive</span>;
    }
    if (now < start) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Scheduled</span>;
    }
    if (now > end) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Expired</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
          <p className="text-gray-600 mt-1">Create and manage discount coupons</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          <Plus className="w-4 h-4 mr-2" />
          New Coupon
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Valid Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Loading coupons...
                  </TableCell>
                </TableRow>
              ) : coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No coupons created yet
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{coupon.code}</span>
                        {coupon.returningCustomersOnly && <span className="text-xs rounded bg-blue-100 text-blue-800 px-1.5 py-0.5" data-testid="badge-returning">returning</span>}
                        {coupon.firstPurchaseOnly && <span className="text-xs rounded bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid="badge-first">first purchase</span>}
                        <button onClick={() => copyCode(coupon.code)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{coupon.type.replace('_', ' ')}</TableCell>
                    <TableCell>
                      {coupon.type === 'percentage' ? `${coupon.value}%` : 
                       coupon.type === 'fixed' ? `$${coupon.value}` : 'Free'}
                    </TableCell>
                    <TableCell>
                      {coupon.usedCount}/{coupon.usageLimit || '∞'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(coupon.startDate)} - {formatDate(coupon.endDate)}
                    </TableCell>
                    <TableCell>{getStatusBadge(coupon)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(coupon)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(coupon.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Coupon Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER2024"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type *</label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="free_shipping">Free Shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {formData.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'} *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Min Order Amount ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.minOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minOrderAmount: e.target.value })}
                />
              </div>

              {formData.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Max Discount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.maxDiscount}
                    onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Start Date *</label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">End Date *</label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Total Usage Limit</label>
                <Input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Per User Limit *</label>
                <Input
                  type="number"
                  value={formData.perUserLimit}
                  onChange={(e) => setFormData({ ...formData, perUserLimit: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2 rounded-md border p-3" data-testid="coupon-customer-rules">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" data-testid="coupon-returning-only"
                       checked={formData.returningCustomersOnly}
                       onChange={(e) => setFormData({ ...formData, returningCustomersOnly: e.target.checked, firstPurchaseOnly: e.target.checked ? false : formData.firstPurchaseOnly })} />
                <span><strong>Returning customers only</strong> — only customers who have already bought (use it for a "next order" coupon)</span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" data-testid="coupon-first-only"
                       checked={formData.firstPurchaseOnly}
                       onChange={(e) => setFormData({ ...formData, firstPurchaseOnly: e.target.checked, returningCustomersOnly: e.target.checked ? false : formData.returningCustomersOnly })} />
                <span><strong>First purchase only</strong> — only customers who have not bought yet (a welcome coupon)</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Internal notes about this coupon"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#be1f1a] hover:bg-[#a71b17]">
                {editingCoupon ? 'Update' : 'Create'} Coupon
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoupons;
